/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */
'use strict';

function MVPDPartnerGridSearchViewController(
    this: JS5Object,
    _: JS5Object,
    $location: JS5Object,
    $route: JS5Object,
    $routeParams: JS5Object,
    uiGridConstants: JS5Object,
    BreadcrumbService: JS5Object,
    ContentSearchService: JS5Object,
    MVPDViewService: JS5Object,
    NavbarUtilService: JS5Object,
    NetworkViewService: JS5Object,
    PaginationService: JS5Object,
    PermissionsService: JS5Object,
    SearchFilterService: JS5Object,
) {
	let initializeBreadcrumbs = () => {
		BreadcrumbService.clearBreadcrumbs();
		BreadcrumbService.addBreadcrumb({
			name: 'MVPD Partners',
			location: $route.current.rootRoute
		});
	};

	let processGridFilter = (renderableRows: JS5Object[]): JS5Object[] => {
		_.forEach(renderableRows, (row: JS5Object): void => {
			if (this.searchText === null || this.searchText === '') {
				row.visible = true;
			} else {
				let criteria: string = this.searchText.toLowerCase();
				row.visible = row.entity.name && row.entity.name.toLowerCase().indexOf(criteria) !== -1;
			}
		});
		return renderableRows;
	};

	let overrideUpdateCurrentPage = (tab: string): void => {
		if (PaginationService.pageNumberMap) {
			if (!PaginationService.pageNumberMap[tab]) {
				PaginationService.pageNumberMap[tab] = 1;
			} else {
				this.pagingOptions.currentPage = PaginationService.pageNumberMap[tab];
			}
		} else {
			PaginationService.pageNumberMap = {};
			PaginationService.pageNumberMap[tab] = 1;
		}
	};

	let navigateToPartnerMetadataEditView = (groupId: JS5Object): void => {
		$location.url(`/meta/mvpd/edit/partner/${$routeParams.id}/${$routeParams.groupName}/${groupId}/metadata/properties`);
	};

	let onPaginationChanged = (): void => {
		getData();
	};

	let handleGridDataQuery = (gridData: JS5Object, responseHeaders: JS5Function) => {
		PaginationService.parseResponseHeaders(responseHeaders(), $routeParams.groupName);
		PaginationService.pageData = gridData;
	};

	let getSearchCriteria = (content: string): JS5Object => {
		let criteria: JS5Object = {
			mvpdPartnerId: $routeParams.id,
			mvpdOnly: !this.showAll,
            searchString: this.searchText
		};

		if ($routeParams.groupName === 'networks') {
			criteria.fetchLimit = 0
		} else {
			criteria.fetchOffset = this.pagingOptions.pageSize * (this.pagingOptions.currentPage - 1);
			criteria.fetchLimit = this.pagingOptions.pageSize;
			criteria.type = content;
		}

		return criteria;
	};

	let getData = (): void => {
		this.loadingIndicator.show = true;
		this.searchText = $location.search().searchText || null;

		let groupPromises: JS5Object = {
			networks: (): void => NetworkViewService.getSearchViewData(getSearchCriteria(undefined), handleGridDataQuery),
			series: (): void => {
				return ContentSearchService.getSearchViewData(getSearchCriteria('Series'), handleGridDataQuery)
			},
			movies: (): void => {
				return ContentSearchService.getSearchViewData(getSearchCriteria('Movie/Special'), handleGridDataQuery)
			}
		};

		groupPromises[$routeParams.groupName]().then((groupData: JS5Object[]): void => {
			this.gridOptions.data = groupData;
			this.loadingIndicator.show = false;
		});
	};

	this.executeSearch = (): void => {
		this.gridApi.grid.refresh();
	};

	this.paginationPageSizeChanged = () => {
		this.pagingOptions.currentPage = 1;
		onPaginationChanged();
	};

	this.paginationPrevPageHandler = () => {
		--this.pagingOptions.currentPage;
		onPaginationChanged();
	};

	this.paginationNextPageHandler = () => {
		++this.pagingOptions.currentPage;
		onPaginationChanged();
	};

	this.hasMetadataProperties = (entity: JS5Object) => {
		return !_.isEmpty(entity.metadata.properties);
	};

	this.onRowDblClick = (row: JS5Object) => {
        navigateToPartnerMetadataEditView(row.entity.id);
	};

	this.getFieldErrorsTooltip = (missingMVPDFields: JS5Object[]): string => {
		return MVPDViewService.getFieldErrorsTooltip(missingMVPDFields);
	};

	this.columnDefinitions = [
		{
			field: 'name',
			displayName: 'Name',
			sort: {
				direction: 'asc',
			},
			cellTemplate: 'components/visual/grid/mvpd-required-error-template.html'
		}
	];

	this.gridOptions = {
		rowSelection: true,
		enableRowHeaderSelection: false,
		enableSelectAll: false,
		enableColumnResizing: true,
		enablePaginationControls: false,
		enableSorting: true,
		enableColumnMenus: false,
		enableFullRowSelection: true,
		enableHorizontalScrollbar: uiGridConstants.ALWAYS,
		enableVerticalScrollbar: uiGridConstants.ALWAYS,
		rowHeight: 26,
		multiSelect: false,
		columnDefs: this.columnDefinitions,
		onRegisterApi: (gridApi: JS5Object): void => {
			this.gridApi = gridApi;
			this.gridApi.grid.registerRowsProcessor(processGridFilter, 200);
		},
		rowTemplate: `<div ng-repeat="(colRenderIndex, col) in colContainer.renderedColumns track by col.uid"
						   ng-class="{ 'ui-grid-cell-hidden' : row.entity.hidden}"
						   class="ui-grid-cell ng-scope ng-binding"
						   ui-grid-cell
						   ng-dblclick="grid.appScope.$ctrl.onRowDblClick(row)">
					 </div>`,
		data: []
	};

	this.toolbarButtonDefs = [
		{
			label: 'Edit',
			enabled: (): boolean => {
				return this.gridApi && this.gridApi.selection.getSelectedCount() === 1 && this.hasMetadataProperties(this.gridApi.selection.getSelectedRows()[0]);
			},
			onClick: (): void => {
				navigateToPartnerMetadataEditView(this.gridApi.selection.getSelectedRows()[0].id);
			}
		}
	];

	this.toolbarCheckboxDefs = [
		{
			label: 'Show All',
			onUpdate: (newValue: boolean) => {
				this.showAll = newValue;
				getData();
			},
			isChecked: false
		}
	];

	this.onTabSelect = (tab: string): void => {
		$route.updateParams({groupName: tab});
		overrideUpdateCurrentPage(tab);
	};

	this.onTabDeselect = function (tab: string, event: any): void {
		PaginationService.pageNumberMap[tab] = this.pagingOptions.currentPage;
	};

	this.$onInit = () => {
		initializeBreadcrumbs();

		this.loadingIndicator = {
			backdrop: true,
			show: false,
			title: 'Working',
			message: 'Please wait...'
		};

		this.activeTabs = {
			networks: false,
			series: false,
			movies: false
		};

		if ($routeParams.groupName === 'networks') {
			this.paginationDefs = null;
		} else {
			this.paginationDefs = {
				paginationPrevPageHandler: this.paginationPrevPageHandler,
				paginationNextPageHandler: this.paginationNextPageHandler,
				paginationPageSizeChanged: this.paginationPageSizeChanged,
				paginationJumpToPageHandler: onPaginationChanged,
				pagingOptions: PaginationService,
			};
		}

		this.currentTab = $routeParams.groupName;
		this.activeTabs[$routeParams.groupName] = true;
		this.searchText = '';
		this.pagingOptions = PaginationService;
		NavbarUtilService.viewSearchFunction = getData;
        SearchFilterService.populateCriteriaFromLocationSearch();

        PermissionsService.getReadWritePermissions(MVPDViewService.permissionViewState).then((permissions:JS5Object) =>{
            this.permissions = permissions;
            getData();
        });
	};

	this.$onDestroy = (): void => {
		NavbarUtilService.viewSearchFunction = null;
	};
}

angular.module('prism-ui.mvpd').component('mvpdPartnerGridSearchView', {
	templateUrl: 'meta/mvpd/partnerSearchView/mvpd-partner-grid-search-view-template.html',
	controller: MVPDPartnerGridSearchViewController,
	bindings: {
		mvpdConfig: '<'
	}
});
